import { useMemo, useState } from 'react';

/** How a table is sorted: by which column, and whether the largest, latest or Z comes first. */
export type Sort = { key: string; desc: boolean };

/**
 * The columns a table can sort by: the value each one compares, and `desc` when its
 * first click should put the largest or latest first (numbers, dates) rather than A to Z.
 */
export type SortColumns<T> = Record<string, { value: (row: T) => string | number | null | undefined; desc?: boolean }>;

// Text compares as people expect: case and accents ignored, "Casino 2" before "Casino 10".
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const empty = (v: unknown) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

/** The rows in the order `sort` asks for. Empty values always go last; ties keep their order. */
export const sortRows = <T>(rows: T[], columns: SortColumns<T>, sort: Sort | null): T[] => {
  const column = sort ? columns[sort.key] : undefined;
  if (!sort || !column) return rows;
  const dir = sort.desc ? -1 : 1;
  return rows
    .map((row, i) => ({ row, i, v: column.value(row) }))
    .sort((a, b) => {
      if (empty(a.v) || empty(b.v)) return empty(a.v) === empty(b.v) ? a.i - b.i : empty(a.v) ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : collator.compare(String(a.v), String(b.v));
      return c * dir || a.i - b.i;
    })
    .map((x) => x.row);
};

/**
 * A table's sort, in the page: the list's own order at first, then the column last
 * clicked; clicking it again reverses it.
 */
export const useSort = <T>(rows: T[], columns: SortColumns<T>, initial: Sort | null = null) => {
  const [sort, setSort] = useState<Sort | null>(initial);
  const sorted = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort]);
  const toggle = (key: string) => setSort((s) => (s?.key === key ? { key, desc: !s.desc } : { key, desc: Boolean(columns[key]?.desc) }));
  return { rows: sorted, sort, toggle };
};

/** A date for sorting: its time in milliseconds, or nothing. */
export const dateValue = (iso: string | null | undefined) => (iso ? Date.parse(iso) : undefined);
